import { Injectable } from '@angular/core';
import { CmAuthService } from '@ats/cm/core';
import { AtsResponsiveService, atsSort } from '@ats/utility';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { NavNode } from './nav-node.interface';

enum NavActionTypes {
	UPDATE_NAV_VISIBILITY = 'Update nav panel visibility',
	UPDATE_PAGE_NAV_VISIBILITY = 'Update page level nav visibility',
	UPDATE_PRIMARY_NAV = 'Update primary nav',
	CLOSE_NAV = 'Close navigation panel',
	UPDATE_NAV_DISABLED = 'Update nav disabled state',
}

interface NavAction {
	readonly type: NavActionTypes;
	readonly payload: boolean | NavNode[];
}

export interface IHeaderLocs {
	key: string;
	default: string;
}

class NavState {
	navVisible = true;
	pageNavVisible = true;
	primaryNav: NavNode[] = [];
	navDisabled = false;
}

const reduce = (state: NavState, action: NavAction) => {
	switch (action.type) {
		case NavActionTypes.UPDATE_NAV_VISIBILITY:
			return {
				...state,
				navVisible: action.payload as boolean,
			};
		case NavActionTypes.CLOSE_NAV:
			return {
				...state,
				navVisible: false,
			};
		case NavActionTypes.UPDATE_PAGE_NAV_VISIBILITY:
			return {
				...state,
				pageNavVisible: action.payload as boolean,
			};
		case NavActionTypes.UPDATE_PRIMARY_NAV:
			return {
				...state,
				primaryNav: (action.payload as NavNode[]).map(node => ({ ...node })),
			};
		case NavActionTypes.UPDATE_NAV_DISABLED:
			return {
				...state,
				navDisabled: action.payload as boolean,
			};
	}
};

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class CmNavigationService {
	private state = new NavState();
	private dispatch = new BehaviorSubject<NavState>(this.state);
	private headerLocalization = new BehaviorSubject<IHeaderLocs>({ key: '', default: '' });

	navVisible$: Observable<boolean> = this.dispatch.asObservable().pipe(
		map(state => state.navVisible),
		distinctUntilChanged()
	);
	pageNavVisible$: Observable<boolean> = this.dispatch.asObservable().pipe(
		map(state => state.pageNavVisible),
		distinctUntilChanged()
	);
	navDisabled$: Observable<boolean> = this.dispatch.asObservable().pipe(
		map(state => state.navDisabled),
		distinctUntilChanged()
	);

	nav$: Observable<NavNode[]> = this.dispatch.asObservable().pipe(map(state => state.primaryNav));

	constructor(private responsiveService: AtsResponsiveService, private auth: CmAuthService) {
		this.responsiveService.breakpoints$.pipe(take(1)).subscribe(breakpoints => {
			// navVisible starts as true. This sets it to false on the initial load if mobile
			if (breakpoints.mobile) {
				this.toggleNav();
			}
			if (breakpoints.small) {
				this.togglePageNav();
			}
		});

		this.auth.token$
			.pipe(untilDestroyed(this), filter(Boolean), withLatestFrom(this.responsiveService.breakpoints$))
			.subscribe(([auth, breakpoint]) => {
				this.state = new NavState();
				this.state.navVisible = !breakpoint.mobile;
				this.dispatch.next(this.state);
			});
	}

	loadNav$(nav$: Observable<NavNode[]>): Observable<NavNode[]> {
		return nav$.pipe(
			tap(nav => {
				const type = NavActionTypes.UPDATE_PRIMARY_NAV;
				const action = { payload: nav, type };
				this.dispatch.next((this.state = reduce(this.state, action)));
			})
		);
	}

	getPageNav$(path: string, exclude: string[] = [], relativeTo: string = ''): Observable<NavNode[]> {
		return this.nav$.pipe(
			map(nodes =>
				nodes
					.filter(
						node =>
							node.url.includes(path) && (!exclude.length || !exclude.some(ex => node.url.includes(ex)))
					)
					.map(node => ({
						...node,
						route: relativeTo + node.url.replace(path, ''),
					}))
			),
			atsSort('displayOrder')
		);
	}

	toggleNav() {
		const type = NavActionTypes.UPDATE_NAV_VISIBILITY;
		const action = { payload: !this.state.navVisible, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	closeNav() {
		const type = NavActionTypes.CLOSE_NAV;
		const action = { payload: false, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	togglePageNav() {
		const type = NavActionTypes.UPDATE_PAGE_NAV_VISIBILITY;
		const action = { payload: !this.state.pageNavVisible, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	updateNavDisabled(navDisabled: boolean) {
		const type = NavActionTypes.UPDATE_NAV_DISABLED;
		const action = { payload: navDisabled, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}
}
